import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { track } from '../../../../lib/util/tracking';
import {
  highlightHtml,
  HighlightedChapter,
  TranscriptChapter,
} from './transcriptHelpers';

type Params = {
  chapters: TranscriptChapter[];
  entriesContainerRef: React.RefObject<HTMLDivElement | null>;
  videoId?: number;
};

/**
 * Owns transcript search: the query, the current-match cursor, in-place
 * highlighting (with a match count derived from the same pass), match
 * navigation, and debounced search tracking.
 */
export function useCaptionsSearch({
  chapters,
  entriesContainerRef,
  videoId,
}: Params) {
  const intl = useIntl();
  const [search, setSearch] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const searchTrackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const query = search.trim().toLowerCase();

  // Highlights matches in place (never hides chapters/entries). matchCount is
  // derived here, not counted from the DOM, so it can't drift from the marks.
  const { chaptersWithHighlights, matchCount } = useMemo(() => {
    let running = 0;
    const mapped: HighlightedChapter[] = chapters.map((chapter) => ({
      chapter,
      entries: chapter.entries.map((entry) => {
        const { html, count } = highlightHtml(
          entry.text,
          query,
          running,
          currentMatchIndex,
        );
        running += count;
        return { entry, html };
      }),
    }));
    return { chaptersWithHighlights: mapped, matchCount: running };
  }, [chapters, query, currentMatchIndex]);

  // Resets to the first match on every query change, batched with setSearch
  // (not in an effect) so the ring lands on the first result immediately.
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setCurrentMatchIndex(0);
  };

  // Debounced so tracking fires once the user pauses, not every keystroke.
  useEffect(() => {
    if (searchTrackTimeoutRef.current)
      clearTimeout(searchTrackTimeoutRef.current);
    if (!query) return undefined;

    searchTrackTimeoutRef.current = setTimeout(() => {
      track('transcript searched', { video_id: videoId, query }, [
        'keen',
        'pendo',
      ]);
    }, 500);

    return () => {
      if (searchTrackTimeoutRef.current) {
        clearTimeout(searchTrackTimeoutRef.current);
      }
    };
  }, [query, videoId]);

  // Scrolls the first match into view on typing, but only if it's off-screen —
  // mirrors Chrome's find-in-page (never yanks the page if the match is
  // already visible, but doesn't leave the user searching a spot they can't
  // see either). Keyed on `query` (not `currentMatchIndex`) so it fires once
  // per new search, not on every explicit next/prev navigation.
  useEffect(() => {
    if (!query || matchCount === 0) return;
    const firstMark = entriesContainerRef.current?.querySelectorAll('mark')[0];
    if (!firstMark) return;

    const rect = firstMark.getBoundingClientRect();
    const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
    if (!isVisible) {
      firstMark.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [query, matchCount, entriesContainerRef]);

  const navigateSearch = (direction: 1 | -1) => {
    if (!matchCount) return;
    const next = (currentMatchIndex + direction + matchCount) % matchCount;
    setCurrentMatchIndex(next);
    entriesContainerRef.current
      ?.querySelectorAll('mark')
      [next]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      navigateSearch(event.shiftKey ? -1 : 1);
    } else if (event.key === 'Escape') {
      handleSearchChange('');
    }
  };

  const searchCountLabel = !query
    ? ''
    : matchCount > 0
      ? `${currentMatchIndex + 1} / ${matchCount}`
      : intl.formatMessage({
          id: 'learn.captions_transcript.no_matches',
          defaultMessage: 'No matches',
        });

  return {
    search,
    query,
    chaptersWithHighlights,
    matchCount,
    isSearchBarStickable: !!query,
    searchCountLabel,
    handleSearchChange,
    handleSearchKeyDown,
    navigateSearch,
  };
}
